//
// Helper pour générer les schemas JSON-LD (Schema.org)
// Supporte : Article, Person, BreadcrumbList, WebSite
//
// Usage :
//   import { buildArticleSchema, buildPersonSchema, buildBreadcrumbSchema, buildWebSiteSchema } from '$lib/jsonld';

import { SITE_URL } from './constants/links';
import { sameAs } from './constants/seo';

export const AUTHOR_NAME = 'Juslin Komenan';
export const AUTHOR_URL = 'https://juslin.dev';
export const AUTHOR_IMG =
	'https://www.juslin.dev/_app/immutable/assets/Gemini_Generated_juslin.Co0opX4b.avif';

function buildPersonNode() {
	return {
		'@type': 'Person',
		name: AUTHOR_NAME,
		url: AUTHOR_URL,
		image: AUTHOR_IMG,
		jobTitle: 'Product Engineer',
		worksFor: {
			'@type': 'Organization',
			name: 'juslin.dev',
			url: SITE_URL
		},
		address: {
			'@type': 'PostalAddress',
			addressLocality: 'Abidjan',
			addressCountry: 'CI'
		},
		sameAs // ← URLs sociales, pas keywords
	};
}
// ─────────────────────────────────────────────
// 1. Person — à inclure sur toutes les pages
// ─────────────────────────────────────────────
export function buildPersonSchema() {
	return {
		'@context': 'https://schema.org',
		...buildPersonNode()
	};
}

// ─────────────────────────────────────────────
// 2. WebSite — page d'accueil uniquement
// ─────────────────────────────────────────────
export function buildWebSiteSchema() {
	return {
		'@context': 'https://schema.org',
		'@type': 'WebSite',
		name: 'juslin.dev',
		url: SITE_URL,
		description:
			"Product Engineer à Abidjan — articles sur le produit, le code et l'entrepreneuriat tech en Afrique.",
		author: buildPersonNode(),
		inLanguage: 'fr-FR',
		potentialAction: {
			'@type': 'SearchAction',
			target: `${SITE_URL}/resources?q={search_term_string}`,
			'query-input': 'required name=search_term_string'
		}
	};
}

// ─────────────────────────────────────────────
// 3. Article — pages de blog
// ─────────────────────────────────────────────
interface ArticleSchemaInput {
	title: string;
	excerpt: string;
	slug: string;
	published: boolean;
	createdAt: Date; // ISO date "2025-03-15"
	// publishedAt: Date; // ISO date "2025-03-15"
	updatedAt?: Date; // ISO date
	category?: string;
	tags?: string[];
	authorId: string;
	readingTime?: number; // en minutes
}

export function buildArticleSchema(article: ArticleSchemaInput) {
	const articleUrl = `${SITE_URL}/resources/${article.slug}`;
	const ogImage = `${SITE_URL}/og.png?title=${encodeURIComponent(article.title)}&excerpt=${encodeURIComponent(article.excerpt)}&type=article${article.category ? `&category=${encodeURIComponent(article.category)}` : ''}`;

	return {
		'@context': 'https://schema.org',
		'@type': 'Article',
		headline: article.title,
		description: article.excerpt,
		url: articleUrl,
		datePublished: article.createdAt,
		dateModified: article.updatedAt ?? article.createdAt,
		inLanguage: 'fr-FR',
		author: buildPersonNode(), // ← sans @context
		publisher: buildPersonNode(), // ← sans @context
		image: {
			'@type': 'ImageObject',
			url: ogImage,
			width: 1200,
			height: 630
		},
		mainEntityOfPage: {
			'@type': 'WebPage',
			'@id': articleUrl
		},
		...(article.category && {
			articleSection: article.category
		}),
		...(article.tags &&
			article.tags.length > 0 && {
				keywords: article.tags.join(', ')
			}),
		...(article.readingTime && {
			timeRequired: `PT${article.readingTime}M`
		})
	};
}

// ─────────────────────────────────────────────
// 4. BreadcrumbList — fil d'Ariane
// ─────────────────────────────────────────────
interface BreadcrumbItem {
	name: string;
	url: string;
}

export function buildBreadcrumbSchema(items: BreadcrumbItem[]) {
	return {
		'@context': 'https://schema.org',
		'@type': 'BreadcrumbList',
		itemListElement: items.map((item, index) => ({
			'@type': 'ListItem',
			position: index + 1,
			name: item.name,
			item: item.url
		}))
	};
}

// ─────────────────────────────────────────────
// Utilitaire — sérialise un ou plusieurs schemas
// ─────────────────────────────────────────────
export function serializeSchema(schema: object | object[]): string {
	return JSON.stringify(schema, null, 0);
}

// Ajoute à la fin de src/lib/jsonld.ts

// ─────────────────────────────────────────────
// 5. Service — page /solutions
// ─────────────────────────────────────────────
export function buildServiceSchema() {
	return {
		'@context': 'https://schema.org',
		'@type': 'Service',
		name: 'Solutions Développement & Conseil Digital',
		url: `${SITE_URL}/solutions`,
		description:
			"Développement sur-mesure, conseil produit, audit technique et formation équipe. De l'idée au produit rentable en 90 jours.",
		provider: buildPersonNode(),
		areaServed: {
			'@type': 'Place',
			name: 'Afrique / Remote'
		},
		serviceType: ['Développement Web', 'Conseil Produit', 'Audit Technique', 'Formation Équipe'],
		offers: {
			'@type': 'Offer',
			price: '0',
			priceCurrency: 'EUR',
			name: 'Diagnostic gratuit 30 minutes',
			url: `${SITE_URL}/solutions`
		}
	};
}

// ─────────────────────────────────────────────
// 6. EducationalOrganization — page /academy
// ─────────────────────────────────────────────
export function buildAcademySchema() {
	return {
		'@context': 'https://schema.org',
		'@type': 'EducationalOrganization',
		name: 'Juslin Academy',
		url: `${SITE_URL}/academy`,
		description:
			'Formations pratiques pour lancer un produit digital, maîtriser SvelteKit et appliquer la méthode IMPACT. 200+ personnes formées.',
		founder: buildPersonNode(),
		location: {
			'@type': 'Place',
			name: 'Abidjan / Remote'
		},
		hasOfferCatalog: {
			'@type': 'OfferCatalog',
			name: 'Formations Juslin Academy',
			itemListElement: [
				{
					'@type': 'Offer',
					itemOffered: {
						'@type': 'Course',
						name: 'Formation en ligne',
						description: 'Apprends à ton rythme. Contenu vidéo + exercices pratiques.',
						provider: buildPersonNode()
					}
				},
				{
					'@type': 'Offer',
					itemOffered: {
						'@type': 'Course',
						name: 'Bootcamp intensif',
						description: '3 jours pour maîtriser SvelteKit et lancer ton premier projet.',
						provider: buildPersonNode()
					}
				},
				{
					'@type': 'Offer',
					itemOffered: {
						'@type': 'Course',
						name: 'Mentorat 1-to-1',
						description: '4 sessions privées pour accélérer ton projet spécifique.',
						provider: buildPersonNode()
					}
				}
			]
		}
	};
}

// ─────────────────────────────────────────────
// 7. FAQPage — toutes les pages
// ─────────────────────────────────────────────
interface FaqItem {
	question: string;
	answer: string;
}

export function buildFaqSchema(items: FaqItem[]) {
	return {
		'@context': 'https://schema.org',
		'@type': 'FAQPage',
		mainEntity: items.map((item) => ({
			'@type': 'Question',
			name: item.question,
			acceptedAnswer: {
				'@type': 'Answer',
				text: item.answer
			}
		}))
	};
}
